import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import QBRLoadingOverlay from '../components/QBRLoadingOverlay';
import { apiClient } from '../lib/apiClient';
import { searchLoadingState } from '../hooks/useSearch';
import { 
  QBRResponse, 
  QBRStatus, 
  QBRUpdate,
  ValueQuote,
  QBRTemplate
} from '../types/api';
import { 
  ArrowLeft,
  Edit,
  Save,
  Download,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  Users,
  Quote,
  TrendingUp,
  AlertTriangle,
  Target,
  ExternalLink,
  Copy,
  Trash2,
  FileText,
  X
} from 'lucide-react';
import SlackIcon from '../components/icons/SlackIcon';
import GongIcon from '../components/icons/GongIcon';
import JiraIcon from '../components/icons/JiraIcon';

const QBRDetailPage: React.FC = () => {
  const { customerId, reportId } = useParams<{ customerId: string; reportId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [report, setReport] = useState<QBRResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedContent, setEditedContent] = useState<QBRUpdate>({});
  
  // QBR-specific loading state
  const [isQBRGenerating, setIsQBRGenerating] = useState(false);
  const [qbrProgress, setQBRProgress] = useState(0);
  const [qbrStatus, setQBRStatus] = useState('');
  
  // Template selection for PDF export
  const [templates, setTemplates] = useState<QBRTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  // Determine the correct back route based on how the user arrived
  const getBackRoute = () => {
    // Use browser history to determine where user came from
    const state = location.state as { from?: string } | null;
    
    // If we have navigation state indicating where user came from
    if (state?.from) {
      return state.from;
    }
    
    // Check if user came from the main QBR page by looking at referrer
    const referrer = document.referrer;
    
    // If user came from main QBR page (/qbrs), go back there
    if (referrer && referrer.includes('/qbrs') && !referrer.includes('/customers/')) {
      return '/qbrs';
    }
    
    // If user came from customer detail page, go back to customer detail page
    if (referrer && referrer.includes(`/customers/${customerId}`) && !referrer.includes('/qbrs')) {
      return `/customers/${customerId}`;
    }
    
    // If customerId is in the URL, go back to customer-specific QBR page
    if (customerId) {
      return `/customers/${customerId}/qbrs`;
    }
    
    // Default fallback to general QBR page
    return '/qbrs';
  };

  const loadReport = useCallback(async () => {
    if (!reportId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.getQBRReport(reportId);
      setReport(response);
      
      // Initialize edited content with current report data
      setEditedContent({
        executive_summary: response.executive_summary,
        performance_review: response.performance_review,
        value_realized_quotes: response.value_realized_quotes,
        challenges_and_risks: response.challenges_and_risks,
        opportunities: response.opportunities,
        action_plan: response.action_plan
      });
      
      // Check if this is a search-triggered QBR generation
      const locationState = location.state as { qbr_generation?: boolean } | null;
      const isSearchTriggeredQBR = locationState?.qbr_generation === true;
      
      // If report is generating, activate QBR-specific loading overlay
      if (response.status === 'generating') {
        setIsQBRGenerating(true);
        setQBRProgress(0);
        setQBRStatus(isSearchTriggeredQBR ? 'Generating QBR from search...' : 'Starting QBR generation...');
        
        // For search-triggered QBR, keep the global loading state active
        if (isSearchTriggeredQBR) {
          console.log('[QBRDetailPage] Search-triggered QBR generation detected, keeping global loading state active');
        }
      } else {
        // Report is ready, hide QBR loading overlay
        setIsQBRGenerating(false);
        setQBRProgress(100);
        searchLoadingState.decrementLoading();
      }
      
    } catch (err: any) {
      console.error('Failed to load QBR report:', err);
      setError(err.response?.data?.error || 'Failed to load QBR report');
      searchLoadingState.decrementLoading(); // Decrement loading counter on error
    } finally {
      setLoading(false);
    }
  }, [reportId, location.state]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Poll for updates when report is generating
  useEffect(() => {
    if (!report || report.status !== 'generating') {
      return;
    }

    const pollInterval = setInterval(() => {
      console.log('[QBR] Polling for report updates...');
      
      // Check if this is a search-triggered QBR generation
      const locationState = location.state as { qbr_generation?: boolean } | null;
      const isSearchTriggeredQBR = locationState?.qbr_generation === true;
      
      // Simulate progress updates
      setQBRProgress(prev => {
        const newProgress = Math.min(prev + Math.random() * 15, 90);
        if (newProgress < 30) {
          setQBRStatus(isSearchTriggeredQBR ? 'Analyzing customer data from search...' : 'Analyzing customer data...');
        } else if (newProgress < 60) {
          setQBRStatus(isSearchTriggeredQBR ? 'Generating AI insights from search...' : 'Generating AI insights...');
        } else if (newProgress < 90) {
          setQBRStatus(isSearchTriggeredQBR ? 'Compiling quarterly report from search...' : 'Compiling quarterly report...');
        } else {
          setQBRStatus(isSearchTriggeredQBR ? 'Finalizing report from search...' : 'Finalizing report...');
        }
        return newProgress;
      });
      
      loadReport();
    }, 3000); // Poll every 3 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, [report, loadReport, location.state]);

  const handleSave = async () => {
    if (!reportId) return;
    
    try {
      setSaving(true);
      await apiClient.updateQBRReport(reportId, editedContent);
      setEditing(false);
      await loadReport(); // Reload to get updated data
    } catch (err: any) {
      console.error('Failed to save QBR report:', err);
      setError(err.response?.data?.error || 'Failed to save QBR report');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    // Reset edited content to original report data
    if (report) {
      setEditedContent({
        executive_summary: report.executive_summary,
        performance_review: report.performance_review,
        value_realized_quotes: report.value_realized_quotes,
        challenges_and_risks: report.challenges_and_risks,
        opportunities: report.opportunities,
        action_plan: report.action_plan
      });
    }
  };

  const handleRegenerate = async () => {
    if (!reportId) return;
    
    try {
      await apiClient.regenerateQBRReport(reportId);
      await loadReport(); // Reload to get updated status
    } catch (err: any) {
      console.error('Failed to regenerate QBR report:', err);
      setError(err.response?.data?.error || 'Failed to regenerate QBR report');
    }
  };

  const handleFinalize = async () => {
    if (!reportId) return;
    
    try {
      await apiClient.finalizeQBRReport(reportId);
      await loadReport(); // Reload to get updated status
    } catch (err: any) {
      console.error('Failed to finalize QBR report:', err);
      setError(err.response?.data?.error || 'Failed to finalize QBR report');
    }
  };

  const handleDelete = async () => {
    if (!reportId || !window.confirm('Are you sure you want to delete this QBR report?')) {
      return;
    }
    
    try {
      await apiClient.deleteQBRReport(reportId);
      navigate(getBackRoute());
    } catch (err: any) {
      console.error('Failed to delete QBR report:', err);
      setError(err.response?.data?.error || 'Failed to delete QBR report');
    }
  };

  const getStatusBadge = (status: QBRStatus) => {
    const statusConfig = {
      draft: { color: 'bg-yellow-100 text-yellow-800', icon: Edit },
      generating: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      final: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      archived: { color: 'bg-gray-100 text-gray-800', icon: Copy }
    };
    
    const config = statusConfig[status];
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  // Helper function to get source icon based on type
  const getSourceIcon = (sourceType: string) => {
    switch (sourceType.toLowerCase()) {
      case 'slack':
        return <SlackIcon size={16} className="mr-2" />;
      case 'gong':
        return <GongIcon size={16} className="mr-2" />;
      case 'jira':
        return <JiraIcon width={16} height={16} className="mr-2" />;
      default:
        return <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>;
    }
  };

  // Load templates when component mounts
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await apiClient.getQBRTemplates();
        const templatesList = response.templates || [];
        setTemplates(templatesList);
        
        // Set default template (first template marked as default, or first template)
        if (templatesList.length > 0) {
          const defaultTemplate = templatesList.find((t: QBRTemplate) => t.is_default) || templatesList[0];
          setSelectedTemplateId(defaultTemplate.template_id);
        }
      } catch (err) {
        console.error('Failed to load templates:', err);
        // Don't show error - templates are optional
      }
    };
    
    loadTemplates();
  }, []);

  const handleExportPDF = () => {
    if (templates.length === 0) {
      // No templates available, export without template
      exportToPDF(null);
    } else if (templates.length === 1) {
      // Only one template, use it directly
      exportToPDF(templates[0].template_id);
    } else {
      // Multiple templates, show selection modal
      setShowTemplateModal(true);
    }
  };

  const exportToPDF = async (templateId: string | null = null) => {
    if (!reportId || !report) return;
    
    try {
      setIsExportingPDF(true);
      setSaving(true);
      setShowTemplateModal(false);
      
      // Call API to generate PDF with selected template
      const pdfBlob = await apiClient.exportQBRToPDF(reportId, templateId || undefined);
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `QBR_${report.customer_name}_${reportId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Failed to export PDF:', error);
      alert(error.response?.data?.detail || 'Failed to export PDF. Please try again.');
    } finally {
      setSaving(false);
      setIsExportingPDF(false);
    }
  };

  // Show QBR-specific loading overlay when generating
  if (isQBRGenerating) {
    return (
      <QBRLoadingOverlay 
        isVisible={true}
        message={qbrStatus}
        progress={qbrProgress}
      />
    );
  }

  // Show PDF export loading overlay (simple spinner, not QBR generation steps)
  if (isExportingPDF) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="flex flex-col items-center space-y-4">
            <LoadingSpinner size="lg" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Exporting PDF...
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              This may take a minute. Please wait while we generate your PDF.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If report is still loading initially, show nothing (let global LoadingOverlay handle it)
  if (!report) {
    return null;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <div className="text-red-800">{error || 'QBR report not found'}</div>
          </div>
        </div>
        <div className="mt-4">
          <Link to={getBackRoute()} className="text-blue-600 hover:text-blue-800">
            ← Back to QBR Reports
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link 
            to={getBackRoute()}
            className="text-blue-600 hover:text-blue-800 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              QBR Report - {report.customer_name}
            </h1>
            <div className="flex items-center mt-2 space-x-4">
              <div className="flex items-center text-gray-600">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDateRange(report.start_date, report.end_date)}
              </div>
              {getStatusBadge(report.status)}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {report.status === 'draft' && !editing && (
            <>
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                onClick={handleFinalize}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Finalize
              </Button>
            </>
          )}
          
          {editing && (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </>
          )}
          
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={saving || isExportingPDF}
          >
            {isExportingPDF ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => copyToClipboard(JSON.stringify(report, null, 2))}
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
          
          <Button
            variant="outline"
            onClick={handleDelete}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <div className="text-red-800">{error}</div>
          </div>
        </div>
      )}

      {/* Executive Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Executive Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editing ? (
            <textarea
              value={editedContent.executive_summary || ''}
              onChange={(e) => setEditedContent(prev => ({ ...prev, executive_summary: e.target.value }))}
              className="w-full h-32 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="Enter executive summary..."
            />
          ) : (
            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed">
                {report.executive_summary || 'No executive summary available.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Review */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Performance Review
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editing ? (
            <textarea
              value={editedContent.performance_review || ''}
              onChange={(e) => setEditedContent(prev => ({ ...prev, performance_review: e.target.value }))}
              className="w-full h-40 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="Enter performance review..."
            />
          ) : (
            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed">
                {report.performance_review || 'No performance review available.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Value Realized Quotes */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Quote className="w-5 h-5 mr-2" />
            Value Realized Quotes
            <Badge variant="secondary" className="ml-2">
              {report.value_realized_quotes.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {report.value_realized_quotes.length === 0 ? (
            <p className="text-gray-500 italic">No value quotes found for this period.</p>
          ) : (
            <div className="space-y-4">
              {report.value_realized_quotes.map((quote, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                  <blockquote className="text-gray-700 italic mb-2">
                    "{quote.quote}"
                  </blockquote>
                  <div className="text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <Users className="w-3 h-3 mr-1" />
                        {quote.user_name || 'Unknown User'}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {quote.timestamp ? formatDate(quote.timestamp) : 'Unknown Date'}
                      </span>
                      <span className="flex items-center">
                        <ExternalLink className="w-3 h-3 mr-1" />
                        {quote.channel_name || quote.source}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Challenges & Risks */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            Challenges & Risks
            <Badge variant="secondary" className="ml-2">
              {report.challenges_and_risks.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {report.challenges_and_risks.length === 0 ? (
            <p className="text-gray-500 italic">No challenges or risks identified for this period.</p>
          ) : (
            <ul className="space-y-2">
              {report.challenges_and_risks.map((challenge, index) => (
                <li key={index} className="flex items-start">
                  <AlertTriangle className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{challenge}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Opportunities */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Opportunities
            <Badge variant="secondary" className="ml-2">
              {report.opportunities.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {report.opportunities.length === 0 ? (
            <p className="text-gray-500 italic">No opportunities identified for this period.</p>
          ) : (
            <ul className="space-y-2">
              {report.opportunities.map((opportunity, index) => (
                <li key={index} className="flex items-start">
                  <TrendingUp className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{opportunity}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Action Plan */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Action Plan
            <Badge variant="secondary" className="ml-2">
              {report.action_plan.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {report.action_plan.length === 0 ? (
            <p className="text-gray-500 italic">No action items defined for this period.</p>
          ) : (
            <ol className="space-y-2">
              {report.action_plan.map((action, index) => (
                <li key={index} className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{action}</span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Sources */}
      {report.sources && report.sources.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Sources ({report.context_used || report.sources.length} contexts used)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {report.sources.map((source, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center flex-1">
                    {getSourceIcon(source.type)}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        {source.url && source.url !== '#' ? (
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline inline-flex items-center group"
                          >
                            {source.channel || source.title || `${source.type.toUpperCase()} Source`}
                            <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                          </a>
                        ) : (
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {source.channel || source.title || `${source.type.toUpperCase()} Source`}
                          </p>
                        )}
                        <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                          {source.type.toUpperCase()}
                        </span>
                      </div>
                      {source.user_name && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          by <span className="font-semibold text-gray-900 dark:text-gray-100 bg-yellow-100 dark:bg-yellow-900 px-1.5 py-0.5 rounded">{source.user_name}</span>
                          {source.user_email && (
                            <span className="text-gray-500 dark:text-gray-500 ml-1">({source.user_email})</span>
                          )}
                        </p>
                      )}
                      {source.timestamp && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {new Date(parseFloat(source.timestamp) * 1000).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 font-medium ml-4">
                    {Math.round(source.relevance_score * 100)}% relevant
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Report Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">Created:</span>
              <span className="ml-2">{formatDate(report.created_at)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Last Updated:</span>
              <span className="ml-2">{formatDate(report.updated_at)}</span>
            </div>
            {report.generated_at && (
              <div>
                <span className="font-medium text-gray-600">Generated:</span>
                <span className="ml-2">{formatDate(report.generated_at)}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-600">Created By:</span>
              <span className="ml-2">{report.created_by}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Selection Modal for PDF Export */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Select Template for PDF Export
              </h2>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
              {templates.map((template) => (
                <div
                  key={template.template_id}
                  onClick={() => setSelectedTemplateId(template.template_id)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    selectedTemplateId === template.template_id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {template.name}
                        </h3>
                        {template.is_default && (
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {template.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        {template.original_filename} • {(template.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    {selectedTemplateId === template.template_id && (
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowTemplateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => exportToPDF(selectedTemplateId || null)}
                disabled={!selectedTemplateId || saving || isExportingPDF}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {saving || isExportingPDF ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QBRDetailPage;
